import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { User } from '../interfaces/user'
import { fetchUsers } from '../../services/fetchAllUser'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import {
  Eye,
  Calendar,
  ArrowRight,
  Building2,
  PartyPopper,
  Briefcase,
  HeartHandshake,
  MessageCircle,
  Phone,
  Mail,
  Globe,
  MapPin,
  Languages,
  Flag,
  User as UserIcon,
} from 'lucide-react'
import { WhatsApp } from '@mui/icons-material'
import { AiFillHome } from 'react-icons/ai'
import {
  IoIosWarning,
  IoMdArrowDropleftCircle,
  IoMdArrowDroprightCircle,
  IoMdRefresh,
} from 'react-icons/io'
import { MdOutlineKeyboardDoubleArrowRight } from 'react-icons/md'
import formatBelgianPhoneNumber from '../fonctions/formatBelgiumPhone'

const ContactStrip = ({
  user,
}: {
  user: User
  previousId: number
  nextId: number
}) => {
  const serviceWithContacts =
    user.announce ||
    user.establishment ||
    user.libertin ||
    user.event ||
    user.work

  if (!serviceWithContacts) return null

  const hasContacts =
    serviceWithContacts.phone ||
    serviceWithContacts.whatsapp ||
    serviceWithContacts.email ||
    serviceWithContacts.website

  if (!hasContacts) return null

  return (
    <div className='bg-anthracite max-w-7xl mx-auto justify-center text-white py-3 rounded-lg mb-6 flex flex-wrap items-center gap-4'>
      {serviceWithContacts.phone &&
        !serviceWithContacts.callOnly &&
        !serviceWithContacts.smsOnly && (
          <a
            href={`tel:${serviceWithContacts.phone}`}
            className='inline-flex items-center gap-2 text-white hover:text-redpink'
          >
            <Phone className='w-6 h-6 text-redpink' />
            {serviceWithContacts.phone}
          </a>
        )}
      {serviceWithContacts.phone && serviceWithContacts.callOnly && (
        <a
          href={`tel:${serviceWithContacts.phone}`}
          className='inline-flex items-center gap-2 text-white hover:text-redpink'
        >
          <Phone className='w-6 h-6 text-redpink' />
          {formatBelgianPhoneNumber(serviceWithContacts.phone)}
        </a>
      )}

      {serviceWithContacts.whatsapp && (
        <a
          href={`https://wa.me/${serviceWithContacts.whatsapp}`}
          className='inline-flex items-center gap-2 text-white hover:text-redpink'
        >
          <WhatsApp className='w-6 h-6 text-redpink' />
          WhatsApp
        </a>
      )}
      {serviceWithContacts.email && user.announce.emailOnly && (
        <a
          href={`mailto:${serviceWithContacts.email}`}
          className='inline-flex items-center gap-2 text-white hover:text-redpink'
        >
          <Mail className='w-6 h-6 text-redpink' />
          {serviceWithContacts.email}
        </a>
      )}
      {serviceWithContacts.website && (
        <a
          href={
            serviceWithContacts.website.startsWith('http')
              ? serviceWithContacts.website
              : `https://${serviceWithContacts.website}`
          }
          target='_blank'
          rel='noopener noreferrer'
          className='inline-flex items-center gap-2 text-white hover:text-redpink'
        >
          <Globe className='w-6 h-6 text-redpink' />
          {serviceWithContacts.website}
        </a>
      )}
    </div>
  )
}

const ServiceCard = ({
  title,
  icon: Icon,
  itemData,
  type,
  user,
}: {
  title: string
  icon: any
  itemData: any
  type: string
  user: User
}) => {
  if (!itemData) return null
  const hasLocation = itemData.location && itemData.location.length > 0
  const { t } = useTranslation()

  const formatMultipleStatuses = (statuses: string[]) => {
    return statuses
      .map(status => {
        switch (status) {
          case 'libertins ou visiteur':
            return 'libertins-visiteurs'
          case 'private':
            return 'prive'
          case 'escort':
            return 'escort'
          case 'massage':
            return 'massage'
          case 'maison privee':
            return 'maison-privee'
          case 'bars et clubs prive':
            return 'bars-clubs-prives'
          case 'salons de massage':
            return 'salons-massage'
          case 'hotels coquins':
            return 'hotels-coquins'
          case 'saunas prives':
            return 'saunas-prives'
          case 'clubs echangistes':
            return 'clubs-echangistes'
          default:
            return status
        }
      })
      .join('-')
  }

  const formatGender = (gender: string) => {
    switch (gender) {
      case 'woman':
        return 'femme'
      case 'man':
        return 'homme'
      case 'transgender':
        return 'transgenre'
      case 'couple':
        return 'couple'
      default:
        return gender
    }
  }

  const formatURL = (title: string, id: number) => {
    return `${title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')}/${id}`
  }

  const getPath = () => {
    if (type === 'announce') {
      return `/announce/${formatGender(
        user.userProfile.gender,
      )}/${formatMultipleStatuses(itemData.status)}/${formatURL(
        itemData.title,
        itemData.id,
      )}`
    }
    return `/${type}/${formatURL(itemData.title, itemData.id)}`
  }

  return (
    <Link
      to={getPath()}
      className='block w-full bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 mb-4'
    >
      <div className='p-4'>
        <div className='flex items-center justify-between border-b border-gray-100 pb-4 mb-4'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-redpink/10 rounded-lg'>
              <Icon className='w-5 h-5 text-redpink' />
            </div>
            <h3 className='text-base font-semibold text-gray-800'>{title}</h3>
          </div>
          <div className='flex items-center gap-4'>
            {itemData.views > 0 && (
              <span className='flex items-center gap-2 text-gray-600 text-lg'>
                <Eye className='w-6 h-6' />
                {itemData.views}
              </span>
            )}
            <ArrowRight className='w-8 h-8 text-redpink' />
          </div>
        </div>

        {itemData.title && (
          <h4 className='font-medium text-gray-800 mb-2'>{itemData.title}</h4>
        )}

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3'>
          <span className='flex items-center gap-2 text-gray-600'>
            <Calendar className='w-3 h-3' />
            {t('posted-on')}{' '}
            {format(new Date(itemData.createdAt), 'dd MMM yyyy')}
          </span>
          {hasLocation && (
            <span className='flex items-center gap-2 text-gray-600'>
              <MapPin className='w-3 h-3' />
              {itemData.location
                .map((loc: any) => t(`provinces.${loc}`))
                .join(', ')}
            </span>
          )}
        </div>

        {itemData.description && (
          <p className='text-sm text-gray-600 line-clamp-2'>
            {itemData.description}
          </p>
        )}
      </div>
    </Link>
  )
}

const ProfileSection = ({ user }: { user: User }) => {
  const { t } = useTranslation()
  if (!user.userProfile) return null

  const mainProfileFields = [
    { icon: UserIcon, value: user.userProfile.age, label: t('create-ad.age') },
    {
      icon: UserIcon,
      value:
        user.userProfile.gender &&
        t(`create-ad.${user.userProfile.gender.toLowerCase()}`),
      label: t('create-ad.gender'),
    },
    {
      icon: Flag,
      value:
        user.userProfile.nationality &&
        t(`nationalities.${user.userProfile.nationality.toLowerCase()}`),
      label: t('create-ad.nationality'),
    },
    {
      icon: UserIcon,
      value: user.userProfile.size,
      label: t('create-ad.size'),
    },
    {
      icon: UserIcon,
      value: user.userProfile.weight,
      label: t('create-ad.weight'),
    },
    {
      icon: UserIcon,
      value:
        user.userProfile.hairColor &&
        t(`create-ad.${user.userProfile.hairColor}`),
      label: t('create-ad.hair'),
    },
    {
      icon: UserIcon,
      value:
        user.userProfile.eyeColor &&
        t(`create-ad.${user.userProfile.eyeColor}`),
      label: t('create-ad.eyes'),
    },
  ].filter(field => field.value)

  const languages = user.userProfile.languages
    ?.map(lang => t(`languages.${lang.toLowerCase()}`))
    .join(', ')

  return (
    <div className='space-y-4 px-4 pb-5'>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
        {mainProfileFields.map((field, index) => (
          <div key={index} className='flex items-center gap-2 text-gray-700'>
            <field.icon className='w-4 h-4 text-redpink shrink-0' />
            <span className='font-medium'>{field.label}:</span>
            <span>{field.value}</span>
          </div>
        ))}
      </div>

      {languages && (
        <div className='flex items-center gap-2 '>
          <Languages className='w-4 h-4 text-redpink shrink-0' />
          <span className='font-medium'>
            {t('create-ad.spoken-languages')}:
          </span>
          <span>{languages}</span>
        </div>
      )}
    </div>
  )
}

const SearchUserPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()
  const previousId = id ? Number(id) - 1 : 0
  const nextId = id ? Number(id) + 1 : 0

  useEffect(() => {
    const loadUser = async () => {
      try {
        const users = await fetchUsers()
        const foundUser = users.find(u => u.id.toString() === id)
        foundUser ? setUser(foundUser) : setError('User not found')
      } catch (err) {
        setError('Error loading user data')
      } finally {
        setLoading(false)
      }
    }
    loadUser()
  }, [id])

  if (loading)
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-redpink'></div>
      </div>
    )
  if (error || !user)
    return (
      <div>
        <div className='bg-redpink'>
          <div className='max-w-7xl pb-2 mx-auto font-light'>
            <div className='flex pt-2 px-2 lg:px-0'>
              <Link
                to='/'
                className='text-white hover:text-anthracite transition-all'
              >
                <AiFillHome className='text-2xl ' />
              </Link>
              <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
              <span className='text-white'>{t('error')}</span>
            </div>
          </div>
        </div>

        <div className='max-w-7xl mx-auto my-5'>
          <div className='flex items-center justify-center min-h-72 p-5 bg-grisclair rounded-lg'>
            <div className='text-center space-y-4'>
              <IoIosWarning className='text-redpink text-6xl mx-auto mb-4' />
              <h2 className='text-2xl font-semibold text-anthracite mb-2'>
                {t('error-occurred')}
              </h2>
              <p className='text-gray-600 mb-6'>{t('user-not-found')}</p>
              <div className='space-x-0 md:space-x-2 '>
                <Link
                  to='/'
                  className='bg-redpink mb-2 md:mb-0 text-white px-6 py-2 rounded-lg hover:bg-anthracite transition-all inline-flex items-center'
                >
                  <AiFillHome className='mr-2' />
                  {t('dashboard.to-the-site')}
                </Link>
                <button
                  onClick={() => window.location.reload()}
                  className='bg-anthracite text-white px-6 py-2 rounded-lg hover:bg-redpink transition-all inline-flex items-center'
                >
                  <IoMdRefresh className='mr-2' />
                  {t('reload-page')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )

  return (
    <>
      <div className='bg-redpink'>
        <div className='max-w-7xl py-2 mx-auto font-light flex justify-between '>
          <div className='flex pt-2'>
            <Link
              to='/'
              className='text-white hover:text-anthracite transition-all'
            >
              <AiFillHome className='text-2xl' />
            </Link>
            <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />

            <p className='text-white'>{user.nickname}</p>
          </div>
          <div className='flex'>
            <Link to={`/search/user/${previousId}`}>
              <IoMdArrowDropleftCircle className='text-4xl hover:text-white transition-all' />
            </Link>
            <Link to={`/search/user/${nextId}`}>
              <IoMdArrowDroprightCircle className='text-4xl hover:text-white transition-all' />
            </Link>
          </div>
        </div>
      </div>
      <div className='bg-anthracite'>
        <ContactStrip user={user} previousId={previousId} nextId={nextId} />
      </div>
      <div className='max-w-7xl mx-auto py-4'>
        <div className='bg-white rounded-lg p-0 shadow-sm'>
          <div className='mb-8 bg-grisclair rounded-lg'>
            <h1 className='text-xl bg-anthracite py-2 px-4 rounded-t-lg text-white mb-4'>
              {user.nickname}
            </h1>
            <ProfileSection user={user} />
          </div>

          <div>
            {user.announce && (
              <ServiceCard
                title={t('announces')}
                icon={HeartHandshake}
                itemData={user.announce}
                type='announce'
                user={user}
              />
            )}
            {user.establishment && (
              <ServiceCard
                title={t('establishments')}
                icon={Building2}
                itemData={user.establishment}
                type='establishment'
                user={user}
              />
            )}
            {user.libertin && (
              <ServiceCard
                title={t('libertins-announces')}
                icon={MessageCircle}
                itemData={user.libertin}
                type='libertin'
                user={user}
              />
            )}
            {user.event && (
              <ServiceCard
                title={t('events')}
                icon={PartyPopper}
                itemData={user.event}
                type='event'
                user={user}
              />
            )}
            {user.work && (
              <ServiceCard
                title={t('works')}
                icon={Briefcase}
                itemData={user.work}
                type='work'
                user={user}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default SearchUserPage
